import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Multiselect,
} from '@amzn/awsui-components-console';
import InputWithValidation from '../common/InputWithValidation';
import React, { useContext, useEffect, useState } from 'react';
import { fetchSubscribedGroups } from '../apis';
import { DDF_REGIONS } from '../../../lib/constants';
import { useParams, useHistory } from 'react-router-dom';
import { displayErrorContent, isValidInput } from '../../../lib/utils';
import { configureBot, listBotByName } from '../apis';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';

export default function ConfigureBot(props) {
    const { id } = useParams();
    const history = useHistory();
    const { state } = props.location;
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [description, setDescription] = React.useState('');
    const [ownerGroup, setOwnerGroup] = React.useState('');
    const [ownerGroupError, setOwnerGroupError] = React.useState('');
    const [groupsOptions, setGroupsOptions] = React.useState([]);
    const [groupsSelectedOptions, setGroupsSelectedOptions] = React.useState([]);
    const [groupsStatus, setGroupsStatus] = React.useState('pending');
    const [confirmLoading, setConfirmLoading] = React.useState(false);
    const [commonGroupIds, setCommonGroupIds] = React.useState({});

    /**
     * set initial status
     */
    const setInitialStatus = () => {
        setConfirmLoading(true);
        setErrorMessage([]);
    };

    /**
     * validate input
     */
    const validateInput = () => {
        let errorflag = false;
        if (!isValidInput(ownerGroup)) {
            setOwnerGroupError('Owner group is required and should not be empty.');
            errorflag = true;
        }
        return !errorflag;
    };

    /**
     * fetch all available private groups options and set common group id
     */
    const getGroupsOptions = async () => {
        var result = [];
        const commonIds = {};
        for (let i = 0; i < DDF_REGIONS.length; i++) {
            const region = DDF_REGIONS[i];
            const regionName = region.regionName;
            const response = await fetchSubscribedGroups(regionName);
            const groups = response['response'];
            const options = [];
            groups.forEach((group) => {
                if (group.privilege === 'root') {
                    commonIds[regionName] = group.id;
                } else {
                    options.push({ label: group.name, value: `${regionName}_${group.id}`, description: group.id });
                }
            });
            result.push({
                label: regionName,
                options: options,
            });
        }
        setCommonGroupIds(commonIds);
        return result;
    };

    /**
     * get bot by name
     */
    const getBot = async () => {
        const response = await listBotByName(id);
        const bot = response['response'];
        return bot;
    };

    /**
     * set subscribed private groups as selected
     */
    const setSubscribedGroups = () => {
        (async function () {
            setGroupsStatus('loading');
            try {
                let selectedOptions = [];
                let groups = await getGroupsOptions();
                const bot = await getBot();
                const subscribedGroups = bot['subscribedGroups'];
                groups.forEach((group) => {
                    const regionName = group.label;
                    const options = group.options;
                    options.forEach((option) => {
                        const groupIds = subscribedGroups[regionName].map((dict) => dict.id);
                        if (groupIds.includes(option.description)) {
                            selectedOptions.push(option);
                        }
                    });
                });
                setGroupsOptions(groups);
                setGroupsSelectedOptions(selectedOptions);
                setGroupsStatus('finished');
            } catch (e) {
                console.error('Fail to set selected private group(s) with error', e);
                setGroupsStatus('error');
            }
        })();
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        if (groupsStatus === 'loading') {
            displayErrorContent(CLIENT_ERROR, 'Loading subscribed private groups, please wait.', setErrorMessage);
            return;
        }

        if (groupsStatus === 'error') {
            displayErrorContent(
                SERVER_ERROR,
                'Loading subscribed private groups failed, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                setErrorMessage
            );
            return;
        }

        let success = false;
        setInitialStatus();
        try {
            if (!validateInput()) {
                success = false;
            } else {
                let subscribedGroups = {};
                for (let region in commonGroupIds) {
                    subscribedGroups[region] = [commonGroupIds[region]];
                }
                groupsSelectedOptions.forEach((group) => {
                    const [region, groupID] = group.value.split('_');
                    subscribedGroups[region].push(groupID);
                });
                await configureBot(id, description, ownerGroup, subscribedGroups);
                success = true;
            }
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status == 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to configure this bot account, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setConfirmLoading(false);
        if (success) {
            history.push('/api-auth/bot-accounts');
        }
    };

    useEffect(() => {
        if (!id || !state) {
            history.push('/api-auth/bot-accounts');
        } else {
            setDescription(state['description'] || '');
            setOwnerGroup(state['ownerGroup'] || '');
            setSubscribedGroups();
        }
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Bot accounts', href: '#/api-auth/bot-accounts' },
                    { text: 'Configure', href: '#/api-auth/bot-accounts/configure' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/api-auth/bot-accounts">
                                Back
                            </Button>
                            <Button formAction="none" loading={confirmLoading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container header={<Header variant="h2">Bot account details: {id}</Header>}>
                            <SpaceBetween direction="vertical" size="m">
                                <FormField label="Bot account username">
                                    <Input value={id} disabled />
                                </FormField>
                                <FormField
                                    label={
                                        <span>
                                            Description <i>- optional</i>{' '}
                                        </span>
                                    }
                                >
                                    <Input
                                        placeholder="Enter optional description"
                                        value={description}
                                        onChange={(event) => {
                                            setDescription(event.detail.value);
                                        }}
                                    />
                                </FormField>
                                <InputWithValidation
                                    label="Owner group"
                                    type="text"
                                    description="LDAP group that owns this bot account"
                                    errorText={ownerGroupError}
                                    placeholder="Enter owner group"
                                    value={ownerGroup}
                                    setValue={setOwnerGroup}
                                    setError={setOwnerGroupError}
                                ></InputWithValidation>
                                <FormField
                                    label={
                                        <span>
                                            Subscribed private groups <i>- optional</i>{' '}
                                        </span>
                                    }
                                    description="This step may take few seconds when multiple private groups are selected."
                                >
                                    <Multiselect
                                        selectedOptions={groupsSelectedOptions}
                                        expandToViewport
                                        placeholder="Select private groups to subscribe"
                                        loadingText="Loading groups"
                                        errorText="Error fetching groups"
                                        finishedText="End of all results"
                                        empty="No group found"
                                        filteringType="auto"
                                        virtualScroll={true}
                                        statusType={groupsStatus}
                                        options={groupsOptions}
                                        onChange={({ detail }) => {
                                            setGroupsSelectedOptions(detail.selectedOptions);
                                        }}
                                    />
                                </FormField>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
